// @flow
import React, { Component, Fragment } from 'react';
import { injectIntl, type IntlShape } from 'react-intl';
import { connect } from 'react-redux';

import { bindActionCreatorsSafe } from 'util/safeDispatch';
import SetAccountDialog from './SetAccountDialog';
import AccountRenderer from './AccountRenderer';
import NoAccount from './NoAccount';
import SetSystemDialog from './SetSystemDialog';
import { hasMainProductLicense, isDemoMode } from 'util/licenseUtils';

import {
  getActivePanelId,
  getAssetCapCount,
  getExtensisAccount,
  getExtensisSystems,
  getIsPendingResponseFromBonfire,
  getLicensedFeatures,
  getLicenseMaintenanceEndDate,
  getLicenses,
  getMaxSeatCount,
} from 'state/selectors';
import type { Dispatch, License, LicensedFeatures, System } from 'state/types';
import {
  hideDialogPanel,
  showDialogPanel,
} from 'state/commands/dialogPanelCommands';
import { SET_ACCOUNT_PANEL_ID, SET_SYSTEM_PANEL_ID } from 'util/constants';
import {
  addLicenses,
  getExtensisAccountInfo,
  setExtensisSystem,
} from 'state/commands/licenseCommands';

type Props = {
  activePanelId: ?string,
  assetCapCount: number,
  addLicenses: typeof addLicenses,
  extensisAccount: ?string,
  extensisSystems: System[],
  getExtensisAccountInfo: typeof getExtensisAccountInfo,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
  isPendingResponseFromBonfire: boolean,
  licensedFeatures: LicensedFeatures,
  licenseMaintenanceEndDate: ?string,
  licenses: License[],
  maxSeatCount: number,
  setExtensisSystem: typeof setExtensisSystem,
  showDialogPanel: typeof showDialogPanel,
};

class Account extends Component<Props> {
  handleClickSetExtensisAccount: Function;

  constructor() {
    super();
    this.handleClickSetExtensisAccount = this.handleClickSetExtensisAccount.bind(
      this
    );
  }

  componentDidMount() {
    const { extensisAccount, licenses } = this.props;
    // prevent re-fectching if possible, it's annoying to see the spinner
    // when navigating to this page, and it's time-consuming talking to Bonfire
    if (
      hasMainProductLicense(licenses) &&
      !isDemoMode(licenses) &&
      !extensisAccount
    ) {
      this.props.getExtensisAccountInfo();
    }
  }

  handleClickSetExtensisAccount() {
    this.props.showDialogPanel(SET_ACCOUNT_PANEL_ID);
  }

  render() {
    const {
      activePanelId,
      addLicenses,
      extensisAccount,
      extensisSystems,
      hideDialogPanel,
      intl,
      isPendingResponseFromBonfire,
      licenseMaintenanceEndDate,
      setExtensisSystem,
      showDialogPanel,
    } = this.props;

    if (isPendingResponseFromBonfire) {
      return (
        <div className="accountResponsePending">
          <i className="fa fa-circle-o-notch fa-spin fa-3x has-text-primary" />
        </div>
      );
    }

    return (
      <Fragment>
        {extensisAccount ? (
          <AccountRenderer
            extensisAccount={extensisAccount}
            clickSetExtensisAccount={this.handleClickSetExtensisAccount}
            intl={intl}
            licenseMaintenanceEndDate={licenseMaintenanceEndDate}
          />
        ) : (
          <NoAccount
            activePanelId={activePanelId}
            addLicenses={addLicenses}
            hideDialogPanel={hideDialogPanel}
            intl={intl}
            showDialogPanel={showDialogPanel}
          />
        )}

        {activePanelId === SET_SYSTEM_PANEL_ID && (
          <SetSystemDialog
            activePanelId={activePanelId}
            hideDialogPanel={hideDialogPanel}
            intl={intl}
            setExtensisSystem={setExtensisSystem}
            systems={extensisSystems}
          />
        )}

        {activePanelId === SET_ACCOUNT_PANEL_ID && (
          <SetAccountDialog
            activePanelId={activePanelId}
            hideDialogPanel={hideDialogPanel}
          />
        )}
      </Fragment>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    assetCapCount: getAssetCapCount(state),
    extensisAccount: getExtensisAccount(state),
    extensisSystems: getExtensisSystems(state),
    isPendingResponseFromBonfire: getIsPendingResponseFromBonfire(state),
    licenseMaintenanceEndDate: getLicenseMaintenanceEndDate(state),
    licensedFeatures: getLicensedFeatures(state),
    licenses: getLicenses(state),
    maxSeatCount: getMaxSeatCount(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      addLicenses,
      getExtensisAccountInfo,
      hideDialogPanel,
      setExtensisSystem,
      showDialogPanel,
    })
)(injectIntl(Account));



// WEBPACK FOOTER //
// ./src/view/Pages/LicensesPage/Account.js